/**
 * Service helpers das Requisicoes de Separacao de Ferragens.
 * Todas as chamadas passam pelo `request` do AuthContext.
 */

const BASE = "/api/separacao-ferragens/requisicoes";

export const SITUACOES_SEPARACAO = [
  { value: "PENDENTE", label: "Pendente", tone: "warning" },
  { value: "EM_SEPARACAO", label: "Em separacao", tone: "info" },
  { value: "SEPARADA", label: "Separada", tone: "success" },
  { value: "CANCELADA", label: "Cancelada", tone: "neutral" },
];

export const SITUACAO_LABEL = Object.fromEntries(
  SITUACOES_SEPARACAO.map((s) => [s.value, s.label]),
);
export const SITUACAO_TONE = Object.fromEntries(
  SITUACOES_SEPARACAO.map((s) => [s.value, s.tone]),
);

export const SITUACOES_TERMINAIS = new Set(["SEPARADA", "CANCELADA"]);

// ---------------- CRUD ----------------

export function criarRequisicaoSeparacao(request, listaComprasId, observacao = "") {
  return request(BASE, {
    method: "POST",
    body: JSON.stringify({ lista_compras_id: listaComprasId, observacao }),
    silentError: true,
  });
}

export function listarRequisicoes(request, { situacao = "", busca = "", listaCompras = "" } = {}) {
  const params = new URLSearchParams();
  if (situacao) params.set("situacao", situacao);
  if (busca) params.set("busca", busca);
  if (listaCompras) params.set("lista_compras", listaCompras);
  const qs = params.toString();
  return request(`${BASE}${qs ? `?${qs}` : ""}`);
}

export function obterRequisicao(request, id) {
  return request(`${BASE}/${id}`);
}

/**
 * Requisicoes de UMA lista, para quem enviou a lista ao estoque.
 *
 * Nao use `listarRequisicoes` para isso: aquele endpoint e a fila do estoque
 * (gate `separacao_ferragens:view`) e o revisor nao tem essa permissao — ele
 * tomava 403 e a tela ficava muda. Este aqui e gateado por
 * `revisor_ferragens:view` e devolve so a lista pedida.
 */
export function listarRequisicoesDaLista(request, listaId) {
  return request(`${BASE}/por-lista/${listaId}`, { silentError: true });
}

// ---------------- Transicoes ----------------

export function iniciarRequisicao(request, id) {
  return request(`${BASE}/${id}/iniciar`, { method: "POST", silentError: true });
}

export function separarRequisicao(request, id) {
  return request(`${BASE}/${id}/separar`, { method: "POST", silentError: true });
}

export function cancelarRequisicao(request, id, motivo) {
  return request(`${BASE}/${id}/cancelar`, {
    method: "POST",
    body: JSON.stringify({ motivo }),
    silentError: true,
  });
}

export function reabrirRequisicao(request, id) {
  return request(`${BASE}/${id}/reabrir`, { method: "POST", silentError: true });
}

/**
 * Devolve a requisicao a fila de pendentes PRESERVANDO o checklist.
 * Nao confundir com `reabrirRequisicao`, que parte de uma lista ja concluida e
 * zera o progresso de proposito.
 */
export function voltarParaPendentes(request, id) {
  return request(`${BASE}/${id}/voltar-pendentes`, { method: "POST", silentError: true });
}

export function moverEtapaRequisicao(request, id, etapa) {
  return request(`${BASE}/${id}/mover-etapa`, {
    method: "POST",
    body: JSON.stringify({ etapa }),
    silentError: true,
  });
}

/** Autosave do checklist: marca/desmarca 1 item como separado (idempotente). */
export function marcarItemSeparado(request, reqId, itemId, separado) {
  return request(`${BASE}/${reqId}/itens/${itemId}`, {
    method: "PATCH",
    body: JSON.stringify({ separado }),
    silentError: true,
  });
}

// ---------------- Revisao pelo solicitante (2026-08-04) ----------------
//
// Quem enviou a lista ao estoque pode corrigi-la enquanto o almoxarife nao
// concluiu. Enquanto a revisao esta aberta o estoque fica bloqueado — por isso
// FINALIZAR importa tanto quanto iniciar.

/** Abre a revisao: o solicitante edita a lista e o almoxarife fica bloqueado. */
export function iniciarRevisaoRequisicao(request, id) {
  return request(`${BASE}/${id}/iniciar-revisao`, { method: "POST", silentError: true });
}

/** Fecha a revisao e devolve a requisicao ao estoque. */
export function finalizarRevisaoRequisicao(request, id) {
  return request(`${BASE}/${id}/finalizar-revisao`, { method: "POST", silentError: true });
}

/** Acrescenta observacao ao historico (vale ate depois de separada). */
export function registrarObservacaoRequisicao(request, id, texto) {
  return request(`${BASE}/${id}/observacao`, {
    method: "POST",
    body: JSON.stringify({ texto }),
    silentError: true,
  });
}

/**
 * Manda 1 item em falta para o setor de Compras.
 * `quantidade` vazia = usa a quantidade do proprio item da lista.
 */
export function solicitarCompraDeItem(request, reqId, itemId, { quantidade = null, observacao = "" } = {}) {
  return request(`${BASE}/${reqId}/itens/${itemId}/solicitar-compra`, {
    method: "POST",
    body: JSON.stringify({ quantidade, observacao }),
    silentError: true,
  });
}

export function loteRequisicoes(request, ids, acao, motivo = "") {
  return request(`${BASE}/lote`, {
    method: "POST",
    body: JSON.stringify({ ids, acao, motivo }),
    silentError: true,
  });
}

// ---------------- Colunas do kanban ----------------

export function obterColunasKanbanSeparacao(request) {
  return request("/api/separacao-ferragens/colunas-kanban");
}

export function salvarColunasKanbanSeparacao(request, colunas) {
  return request("/api/separacao-ferragens/colunas-kanban", {
    method: "PUT",
    body: JSON.stringify({ colunas }),
    silentError: true,
  });
}
